import { Box, Stack, Typography, CircularProgress } from "@mui/material";
import { useState, useEffect } from "react";
import { useLoaderData, useLocation, useParams } from "react-router-dom";

import ApiRequestErrorHandler from "../../../components/global/ApiRequestErrorHandler";
import GoBackButton from "../../../components/global/GoBack/GoBackButton";
import ResponseModal from "../../../components/global/ResponseModal";
import useService from "../../../hooks/useService";
import FormatISODateTime from "../../global/FormatISODateTime";
import PaginationControls from "../../global/PaginationControls";
import PermissionWrapper from "../../global/PermissionWrapper";
import CaseListChips from "../../modules/CaseList/Filters/Chips";
import FiltersList from "../../modules/CaseList/Filters/FiltersList";
import { useCaseListFilters } from "../../modules/CaseList/hooks/useCaseListFilters";
import {
  FilterListConfig,
  FilterClient,
} from "../../modules/documentsListView/utils/FilterListConfig";
import { ERROR_FETCHING_DOCUMENTS } from "../../modules/documentsListView/utils/strings";
import HNSColors from "../../ui library/HNSColors";
import OpenSendEmailButton from "../Common/EmailModal/components/OpenSendEmailButton";
import { useEmailModalController } from "../Common/EmailModal/hooks/useEmailModalController";
import { useEmailModal } from "../Common/EmailModal/store/useEmailModal";

import DownloadButton from "./components/DownloadButton";
import FiltersButton from "./components/FiltersButton";
import GoClientDetail from "./components/GoClientDetail";
import SearchBar from "./components/SearchBar";
import DocumentsTable from "./components/Table/DocumentsTable";
import UploadButton from "./components/UploadButton";
import useGoClientDetail from "./hooks/useGoClientDetail";
import useSelectDocuments from "./hooks/useSelectDocuments";
import { useViewDocumentsPermissions } from "./hooks/useViewDocumentsPermissions";

export default function DocumentsListView() {
  const location = useLocation();
  const path = location.pathname;
  const isClientDetailPage = path.includes("/client-detail/");
  const isDocumentsPage =
    path.startsWith("/documents") && !path.includes("/finance/cases/") && !isClientDetailPage;
  const { id } = useParams();

  const { updateSelectedDocs, isDownloadAllDocs, selectedDocuments } = useSelectDocuments();
  const { _case, documents: initialDocuments, fetchDocuments, message, count, raw_data } = useLoaderData();
  const { canExportAllDocuments } = useViewDocumentsPermissions();
  const { filterState, activeFilters } = useCaseListFilters(
    isDocumentsPage ? [...FilterListConfig, FilterClient] : FilterListConfig
  );
  const {fetchEmailHistory} = useEmailModalController();
  const {lastEmailReciepient} = useEmailModal();
  const [documents, setDocuments] = useState(initialDocuments);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(message);
  const [errorArray, setErrorArray] = useState([]);
  const [paginationCount, setPaginationCount] = useState(count);
  const documentRepository = useService("repositories/document-repository");

  const loadDocuments = async (page, updatePage = false) => {
    setLoading(true);
    try {
      const newDocuments = await fetchDocuments(page);
      setDocuments(newDocuments);
      const responseTotal = await documentRepository.getTotalCount();
      setPaginationCount(responseTotal);
      if (updatePage) {
        setCurrentPage(page);
      }
    } catch (error) {
      const errArr = ApiRequestErrorHandler(error ?? []);
      setUploadFailed(true);
      setErrorArray(errArr);
    } finally {
      setLoading(false);
    }
   
  };

  useEffect(() => {
    setDocuments(initialDocuments);
    setPaginationCount(count);
    setCurrentPage(1);
    fetchEmailHistory();
  }, [initialDocuments, count]);

  const refreshDocuments = () => loadDocuments(currentPage);
  const refreshDocumentsByPagination = (_e, value) => loadDocuments(value, true);

  const hasDocuments = Array.isArray(documents) && documents.length > 0;
  const isFromDocumentSearch = useGoClientDetail(id, location, path, isClientDetailPage);
  const shouldSeeShareButton = raw_data?.should_see_share_button ?? false;
  return (
    <div className="h-[calc(100vh-5rem)]" style={{ marginBottom: "3rem" }}>
      {(isClientDetailPage || isFromDocumentSearch) && (
        <div className="flex items-center justify-between pb-4">
          {isClientDetailPage ? (
            <div className="[&>button]:!mb-0">
              <GoBackButton
                prevUrlDefault={
                  !isFromDocumentSearch
                    ? `/client-detail/${id}/client-info/images`
                    : `/search`
                }
                forcePrevURLDefault={!isFromDocumentSearch}
              />
            </div>
          ) : (
            <span />
          )}
          {isFromDocumentSearch && <GoClientDetail caseId={id} />}
        </div>
      )}
      <div className="flex h-full max-h-[calc(100vh)] min-h-60 w-full min-w-min flex-row rounded-md bg-white shadow-[3px_3px_10px_0px_rgba(188,209,255)]">
        <div className="relative flex flex-col flex-1 px-6 overflow-hidden py-9">
          <Stack className="flex flex-wrap mb-8">
            <Typography
              fontWeight={600}
              sx={{ color: HNSColors.primary[500] }}
              className="w-full mb-2 text-2xl"
            >
              Documents
            </Typography>
            {lastEmailReciepient && <span className="text-sm text-gray-500">
              Documents shared successfully on: <b className="text-black">{FormatISODateTime(lastEmailReciepient?.sentAt, "", " - ")}</b>{" "}
              by <b className="text-black">{lastEmailReciepient?.actor?.name}</b> to{" "}
              <b className="text-black">{lastEmailReciepient?.recipients?.join(", ")}</b>
            </span>
            }
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <div className="flex gap-2">
              <SearchBar />
              <FiltersButton>
                <FiltersList filterState={filterState} />
              </FiltersButton>

              <PermissionWrapper permission={canExportAllDocuments && !isDocumentsPage && hasDocuments && shouldSeeShareButton}>
                <OpenSendEmailButton
                  buttonText="Share Documents"
                  caseData={_case}
                  templateName="documents"
                />
              </PermissionWrapper>
            </div>
            <Stack direction="row" spacing={2}>
              <PermissionWrapper
                permission={canExportAllDocuments && !isDocumentsPage && hasDocuments}
              >
                <DownloadButton
                  isDownloadAllDocs={isDownloadAllDocs}
                  selectedDocuments={selectedDocuments}
                />
              </PermissionWrapper>
              {!isDocumentsPage && <UploadButton />}
            </Stack>
            <CaseListChips activeFilters={activeFilters} />
          </Stack>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress color="secondary" />
            </Box>
          ) : (
            <>
              <DocumentsTable
                documents={documents}
                updateSelectedDocs={updateSelectedDocs}
                onDocumentUpdate={refreshDocuments}
                isDocumentsPage={isDocumentsPage}
                hasDocuments={hasDocuments}
              />
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <PaginationControls
                  resultCount={{ total: paginationCount }}
                  handleChange={refreshDocumentsByPagination}
                  currentPage={currentPage}
                />
              </Box>
            </>
          )}
        </div>
      </div>
      <ResponseModal
        title={message || ERROR_FETCHING_DOCUMENTS}
        isError={true}
        openBool={uploadFailed}
        setOpenBool={setUploadFailed}
        errorMessage={errorArray}
      />
    </div>
  );
}
